/**
 * System Settings Service - Handles system configuration and AI settings
 */

import { ApiService } from './api';

export interface SystemSetting {
  id: number;
  key: string;
  value: any;
  type: 'string' | 'json' | 'boolean' | 'integer';
  description: string;
  encrypted: boolean;
  is_public: boolean;
  category: string;
  created_at: string;
  updated_at: string;
  has_value?: boolean;
}

export interface SystemSettingsResponse {
  success: boolean;
  message: string;
  data: SystemSetting[];
}

export interface SystemSettingsCategoryResponse {
  success: boolean;
  message: string;
  data: {
    category: string;
    settings: Record<string, any>;
  };
}

export interface BulkUpdateRequest {
  settings: Array<{
    key: string;
    value: any;
    type: 'string' | 'json' | 'boolean' | 'integer';
    encrypted: boolean;
    is_public: boolean;
    category: string;
    description: string;
  }>;
}

export interface BulkUpdateResponse {
  success: boolean;
  message: string;
  data: {
    updated_count: number;
    updated_keys: string[];
  };
}

export interface AIConnectionTestRequest {
  provider: 'claude' | 'openai' | 'openwebui';
}

export interface AIConnectionTestResponse {
  success: boolean;
  message: string;
  data: {
    provider: string;
    configured: boolean;
    connection_status?: string;
    tested_at?: string;
    error?: string;
    url_configured?: boolean;
    api_key_configured?: boolean;
    server_url?: string;
  };
}

export interface InitializeAISettingsResponse {
  success: boolean;
  message: string;
  data: {
    created_count: number;
    total_settings: number;
  };
}

class SystemSettingsService {
  /**
   * Get all system settings
   */
  async getSettings(includeValues: boolean = true, category?: string): Promise<SystemSetting[]> {
    const params = new URLSearchParams();
    if (!includeValues) params.set('include_values', 'false');
    if (category) params.set('category', category);

    const queryString = params.toString();
    const endpoint = `/admin/settings${queryString ? `?${queryString}` : ''}`;

    const response = await ApiService.get<SystemSettingsResponse>(endpoint);
    return response.data;
  }

  /**
   * Get settings by category
   */
  async getSettingsByCategory(category: string): Promise<Record<string, any>> {
    const response = await ApiService.get<SystemSettingsCategoryResponse>(`/admin/settings/category/${category}`);
    return response.data.settings;
  }

  /**
   * Update multiple settings at once
   */
  async updateSettings(settings: BulkUpdateRequest['settings']): Promise<BulkUpdateResponse['data']> {
    const response = await ApiService.put<BulkUpdateResponse>('/admin/settings/bulk', {
      settings
    });
    return response.data;
  }

  /**
   * Update a single setting
   */
  async updateSetting(
    key: string, 
    value: any, 
    type?: string, 
    encrypted?: boolean, 
    isPublic?: boolean, 
    category?: string, 
    description?: string
  ): Promise<void> {
    const updateData: any = { value };
    if (type !== undefined) updateData.type = type;
    if (encrypted !== undefined) updateData.encrypted = encrypted;
    if (isPublic !== undefined) updateData.is_public = isPublic;
    if (category !== undefined) updateData.category = category;
    if (description !== undefined) updateData.description = description;

    await ApiService.put(`/admin/settings/${key}`, updateData);
  }

  /**
   * Delete a setting
   */
  async deleteSetting(key: string): Promise<void> {
    await ApiService.delete(`/admin/settings/${key}`);
  }

  /**
   * Initialize default AI settings
   */
  async initializeAISettings(): Promise<InitializeAISettingsResponse['data']> {
    const response = await ApiService.post<InitializeAISettingsResponse>('/admin/settings/ai/initialize', {});
    return response.data;
  }

  /**
   * Test AI connection with stored credentials
   */
  async testAIConnection(provider: 'claude' | 'openai' | 'openwebui'): Promise<AIConnectionTestResponse['data']> {
    const response = await ApiService.post<AIConnectionTestResponse>('/admin/settings/ai/test-connection', {
      provider
    });
    return response.data;
  }

  /**
   * Get AI settings specifically
   */
  async getAISettings(): Promise<SystemSetting[]> {
    return this.getSettings(true, 'ai');
  }

  /**
   * Get SpecKit settings specifically
   */
  async getSpecKitSettings(): Promise<SystemSetting[]> {
    return this.getSettings(true, 'speckit');
  }

  /**
   * Get integration settings specifically
   */
  async getIntegrationSettings(): Promise<SystemSetting[]> {
    return this.getSettings(true, 'integrations');
  }

  /**
   * Update AI provider settings
   */
  async updateAIProvider(provider: 'claude' | 'openai' | 'openwebui', apiKey: string): Promise<void> {
    await this.updateSetting(
      `ai.${provider}.api_key`,
      apiKey,
      'string',
      true,
      false,
      'ai',
      `${provider.charAt(0).toUpperCase() + provider.slice(1)} API key for SpecKit integration`
    );
  }

  /**
   * Set default AI provider
   */
  async setDefaultAIProvider(provider: 'claude' | 'openai' | 'openwebui' | 'mock'): Promise<void> {
    await this.updateSetting(
      'ai.default_provider',
      provider,
      'string',
      false,
      true,
      'ai',
      'Default AI provider (claude, openai, mock)'
    );
  }

  /**
   * Update SpecKit service configuration
   */
  async updateSpecKitConfig(serviceUrl?: string, timeout?: number): Promise<void> {
    if (serviceUrl !== undefined) {
      await this.updateSetting(
        'speckit.service_url',
        serviceUrl,
        'string',
        false,
        false,
        'speckit',
        'SpecKit service URL'
      );
    }

    if (timeout !== undefined) {
      await this.updateSetting(
        'speckit.timeout',
        timeout.toString(),
        'integer',
        false,
        false,
        'speckit',
        'SpecKit service timeout in seconds'
      );
    }
  }

  /**
   * Update GitHub token
   */
  async updateGitHubToken(token: string): Promise<void> {
    await this.updateSetting(
      'github.token',
      token,
      'string',
      true,
      false,
      'integrations',
      'GitHub personal access token for repository integration'
    );
  }
}

// Export singleton instance
const systemSettingsService = new SystemSettingsService();
export default systemSettingsService;